import { clickLinkFromPortal } from '../../utils/adPortal';

// Fields excluded from the compact DiffTable.
// Internal IDs and complex nested objects that ContentComparisonView renders in full.
export const SKIP_KEYS = new Set([
  // Internal / audit IDs
  'modifiedBy', 'createdBy', 'spaceId', 'id',
  'boothId', 'advertiserId', 'campaignId', 'charityId', 'hostingId',
  'portalId', 'itemId', 'discountId', 'adId',
  'nameKey', 'descKey', 'mediaId', 'taxCategoryId',
  // Approval metadata on the snapshot
  'approvalStatus', 'approvalId', 'approvalActionType',
  // Complex nested structures — ContentComparisonView renders these in full
  'boothItems', 'mediaItems', 'correspondingMedia', 'thumbnailMedia',
  'portalLinks', 'media',
  // Package — nested objects (name/description/thumbnail/price), rendered in full by
  // PackageDetailPanel/PackageItemDetailPanel instead of the generic key-value fallback.
  'packageId', 'thumbnail', 'price', 'members',
  'merchandise', 'badge', 'ticket', 'avatar',
  'prices', 'discounts', 'tax',
  // 3D ad — the request key (the snapshot carries `model`, shown by name) and the derived name of mediaTypeId
  'modelId', 'mediaTypeName',
]);

// Human-readable labels for known proposed_data keys
export const FIELD_LABELS = {
  isActive:               'Active',
  isAvailable:            'Available',
  streamUrl:              'Stream URL',
  websiteUrl:             'Website URL',
  donationUrl:            'Donation URL',
  industry:               'Industry',
  mapSpotId:              'Map Spot',
  startDate:              'Start Date',
  endDate:                'End Date',
  dailyRate:              'Daily Rate',
  costPerDay:             'Cost / Day',
  totalCost:              'Total Cost',
  durationModifier:       'Duration Modifier',
  durationSeconds:        'Duration (sec)',
  placementId:            'Placement',
  sku:                    'SKU',
  baseCost:               'Base Cost',
  itemTypeId:             'Item Type',
  localizedName:          'Name',
  localizedPairs:         'Name',
  localizedDesc:          'Description',
  localizedDescription:   'Description',
  i18nName:               'Name',
  i18nDesc:               'Description',
  nameLocalizations:      'Name',
  descriptionLocalizations: 'Description',
  mediaLinks:             'Media URL',
  portalLinks:            'Portal Links',
  model:                  '3D Model',
  portal:                 'Portal',
};

// "<title> · N links" for a 3D ad's portal (title: English when there is one).
export function portalSummary(portal) {
  const values = Array.isArray(portal?.localizedPairs?.values) ? portal.localizedPairs.values : [];
  const title  = (values.find((v) => v?.localeId === 'en_us') ?? values[0])?.value;
  const count  = Array.isArray(portal?.portalLinks) ? portal.portalLinks.length : 0;
  const links  = `${count} link${count === 1 ? '' : 's'}`;
  return title ? `${title} · ${links}` : links;
}

// What makes two ad portals the same: title, main media and the links by position. Ids, space and status are left out
// (the live portal and the proposed one carry them differently), and so is a thumbnail's media type.
export function normalizePortal(portal) {
  if (!portal || typeof portal !== 'object') return null;
  const pairs = (list, valueKey) => (Array.isArray(list) ? list : [])
    .filter((e) => e?.localeId && String(e[valueKey] ?? '').trim() !== '')
    .map((e) => [e.localeId, String(e[valueKey]).trim()])
    .sort((a, b) => a[0].localeCompare(b[0]));
  const links = (m) => { const l = pairs(m?.linkLocalizations, 'mediaLink'); return l.length ? l : null; };
  const main  = links(portal.correspondingMedia);
  return {
    title: pairs(portal.localizedPairs?.values, 'value'),
    main:  main ? { type: portal.correspondingMedia.mediaTypeId ?? null, links: main } : null,
    links: (Array.isArray(portal.portalLinks) ? portal.portalLinks : [])
      .map((l) => ({ pos: l?.positionId ?? null, url: String(l?.externalLink ?? '').trim(), thumb: links(l?.thumbnailMedia) }))
      .sort((a, b) => (a.pos ?? -1) - (b.pos ?? -1)),
  };
}

export function humaniseKey(key) {
  return FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

// Extracts a human-readable scalar string from any value shape.
export function extractDisplayValue(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;

  if (Array.isArray(val)) {
    if (!val.length) return 'None';
    // [{localeId, value}] — localization text array
    if (val[0]?.value !== undefined) return val[0].value;
    // [{localeId, mediaLink}] — media URL array
    if (val[0]?.mediaLink !== undefined) {
      return val.length === 1
        ? val[0].mediaLink
        : `${val.length} localized link${val.length > 1 ? 's' : ''}`;
    }
    // Array of complex objects (portalLinks, mediaItems, etc.) — show count only
    if (typeof val[0] === 'object') {
      return `${val.length} item${val.length > 1 ? 's' : ''}`;
    }
    return val.join(', ');
  }

  if (typeof val === 'object') {
    // {key, values: [{localeId, value}]} — localizedPairs / localizedName shape
    if (Array.isArray(val.values) && val.values[0]?.value !== undefined) {
      return val.values[0].value;
    }
    if (Array.isArray(val.values)) {
      return val.values.map((v) => v.value).filter(Boolean).join(', ') || '—';
    }
    // Media object with linkLocalizations
    if (Array.isArray(val.linkLocalizations)) {
      return val.linkLocalizations[0]?.mediaLink ?? 'Media';
    }
    // 3D ad model reference { id, modelKey, displayName }
    if (typeof val.modelKey === 'string') {
      return val.displayName ? `${val.displayName} (${val.modelKey})` : val.modelKey;
    }
    // 3D ad portal { localizedPairs, correspondingMedia, portalLinks }
    if (Array.isArray(val.portalLinks)) return portalSummary(val);
    return JSON.stringify(val);
  }

  return String(val);
}

// Finds and extracts the current live value for a proposed_data key.
// Handles aliasing between UpdateDto keys and GET response keys.
export function findLiveValue(liveContent, proposedKey) {
  if (!liveContent) return undefined;

  // Direct key match — covers most scalar fields
  if (proposedKey in liveContent) {
    return extractDisplayValue(liveContent[proposedKey]);
  }

  // Localised name aliases across content types
  if (['localizedName', 'localizedPairs', 'i18nName', 'nameLocalizations'].includes(proposedKey)) {
    const v = liveContent.localizedName
      ?? liveContent.localizedPairs
      ?? liveContent.nameLocalizations;
    return v !== undefined ? extractDisplayValue(v) : undefined;
  }

  // Localised description aliases
  if (['localizedDesc', 'localizedDescription', 'i18nDesc', 'descriptionLocalizations'].includes(proposedKey)) {
    const v = liveContent.localizedDesc
      ?? liveContent.localizedDescription
      ?? liveContent.descriptionLocalizations;
    return v !== undefined ? extractDisplayValue(v) : undefined;
  }

  // Media URL aliases — flat array on proposed vs nested on live
  if (proposedKey === 'mediaLinks') {
    // Ad: proposed uses mediaLinks; live also uses mediaLinks (direct match above handles it)
    // Fallback: check nested shapes
    const v = liveContent.media?.linkLocalizations
      ?? liveContent.correspondingMedia?.linkLocalizations;
    return v !== undefined ? extractDisplayValue(v) : 'None';
  }

  // portalLinks: portal links list
  if (proposedKey === 'portalLinks') {
    const v = liveContent.portalLinks;
    return v !== undefined ? extractDisplayValue(v) : 'None';
  }

  return undefined;
}

// Returns true when current and proposed display values are considered equal.
export function valuesEqual(current, proposed) {
  if (current === undefined || proposed === undefined) return true; // can't determine
  if (current === null && proposed === null) return true;
  const a = typeof current === 'string' ? current.trim() : String(current ?? '');
  const b = typeof proposed === 'string' ? proposed.trim() : String(proposed ?? '');
  return a === b;
}

// ── Game schedule (the Quiz) ─────────────────────────────────────────────────
// A quiz snapshot also carries ids, exact UTC times, the phase and audit fields — none of which an approver compares —
// and its questions are nested, so the generic key-by-key rows would be mostly noise. The compact table shows what an
// edit changes at a glance (name, period, dates, the questions as a whole); the full question-by-question comparison
// is the Side-by-Side view (buildComparisonRows.js).

// Everything an approver would call "the questions": the texts in every language, the options in order, which is correct.
const quizQuestionsFingerprint = (questions) => JSON.stringify(
  (Array.isArray(questions) ? questions : [])
    .slice()
    .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
    .map((q) => [
      (q?.text?.values ?? []).map((v) => [v.localeId, v.value]),
      (q?.options ?? [])
        .slice()
        .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
        .map((o) => [(o?.text?.values ?? []).map((v) => [v.localeId, v.value]), Boolean(o?.isCorrect)]),
    ]),
);

export function buildQuizDiffRows(proposedData, liveContent) {
  let proposed;
  if (proposedData && typeof proposedData === 'object') proposed = proposedData;
  else {
    try { proposed = JSON.parse(proposedData); } catch { return []; }
  }
  if (!proposed || typeof proposed !== 'object') return [];

  const current = liveContent && typeof liveContent === 'object' ? liveContent : null;
  const has = current !== null;
  const dateText = (v) => (v ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${String(v).slice(0, 10)}T00:00:00`)) : null);
  const typeText = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : null);
  const count = (d) => (Array.isArray(d?.questions) ? d.questions.length : 0);
  const countText = (n) => `${n} question${n === 1 ? '' : 's'}`;

  const row = (key, label, cur, prop) => ({
    key, label, current: has ? cur : undefined, proposed: prop, changed: has && String(cur ?? '') !== String(prop ?? ''),
  });

  const questionsChanged = has && quizQuestionsFingerprint(current.questions) !== quizQuestionsFingerprint(proposed.questions);
  const proposedCount = countText(count(proposed));
  const questionsRow = {
    key: 'questions',
    label: 'Questions',
    current: has ? countText(count(current)) : undefined,
    proposed: questionsChanged && count(current) === count(proposed) ? `${proposedCount} (edited)` : proposedCount,
    changed: questionsChanged,
  };

  // The announcement date chosen for the live announcement linked to the quiz (stored in the proposal only when the dates changed).
  const announcementName = current?.linkedAnnouncement?.name;
  const announcementRows = proposed.linkedAnnouncement?.announcementDate
    ? [row('announcementDate', announcementName ? `Announcement Date (${announcementName})` : 'Announcement Date',
        dateText(current?.linkedAnnouncement?.announcementDate), dateText(proposed.linkedAnnouncement.announcementDate))]
    : [];

  return [
    row('name', 'Name', current?.name, proposed.name),
    row('scheduleType', 'Period', typeText(current?.scheduleType), typeText(proposed.scheduleType)),
    row('startDate', 'Start Date', dateText(current?.startDate), dateText(proposed.startDate)),
    row('endDate', 'End Date', dateText(current?.endDate), dateText(proposed.endDate)),
    ...announcementRows,
    questionsRow,
  ].sort((a, b) => Number(b.changed) - Number(a.changed));
}

// Builds a flat row list from proposedData + liveContent for the compact DiffTable in the drawer.
// proposedData may be a JSON string or a pre-parsed object (snapshot-shaped or DTO delta).
// Each row: { key, label, proposed, current, changed }
export function buildDiffRows(proposedData, liveContent) {
  let parsed;
  if (proposedData && typeof proposedData === 'object') {
    parsed = proposedData;
  } else {
    try { parsed = JSON.parse(proposedData); } catch { return []; }
  }
  if (!parsed || typeof parsed !== 'object') return [];

  return Object.entries(parsed)
    .filter(([k]) => !SKIP_KEYS.has(k))
    .map(([key, rawProposed]) => {
      // A 2D ad's portal is just its click link: one "Click Link" row with the URL ('Removed' when the edit takes it away)
      if (key === 'portal' && (parsed.is3d ?? liveContent?.is3d) === false) {
        const liveUrl     = clickLinkFromPortal(liveContent?.portal);
        const proposedUrl = rawProposed == null ? '' : clickLinkFromPortal(rawProposed);
        return { key, label: 'Click Link', proposed: proposedUrl || (liveUrl ? 'Removed' : null), current: liveUrl || null, changed: proposedUrl !== liveUrl };
      }

      let proposed = extractDisplayValue(rawProposed);
      const current = findLiveValue(liveContent, key);
      let changed = !valuesEqual(current, proposed);

      // A 3D ad's portal is compared as a whole (the summary text alone would miss a changed URL).
      if (key === 'portal' && current !== undefined) {
        changed = JSON.stringify(normalizePortal(rawProposed)) !== JSON.stringify(normalizePortal(liveContent.portal));
      }
      // null on the proposed side is a removal when the live ad has one (a 3D ad switched to 2D)
      if (rawProposed == null && current != null) {
        if (key === 'portal') proposed = 'Removed';
        if (key === 'model')  proposed = 'None';
      }
      return { key, label: humaniseKey(key), proposed, current, changed };
    })
    .sort((a, b) => {
      // Changed rows first
      if (a.changed && !b.changed) return -1;
      if (!a.changed && b.changed) return  1;
      return 0;
    });
}

